import React from 'react';
import { BackButton, Button } from 'components/index';
import { LoginFormStep, useLoginForm } from 'features/auth/hooks/useLoginForm';
import { Link, Stack, Typography } from '@mui/material';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { VersionInfo } from 'components/VersionInfo/VersionInfo';
import { useCobrandedPathWithSubpath } from 'features/cobrand/hooks/useCobrandUtils';
import { AccessRestrictionDialog } from 'components/AccessRestrictionDialog/AccessRestrictionDialog';
import { HospitalLogoSplash } from 'components/HospitalLogoSplash/HospitalLogoSplash';
import { useSplash } from 'features/splashScreen/hooks/useSplash';
import {
  LoginEmailStep,
  LoginForgotPasswordStep,
  LoginPasswordResetConfirmationStep,
  LoginPasswordStep,
  LoginStepHeader,
} from './LoginFormComponents';

interface LoginFormProps {
  onLogoClick?: () => void;
}

export const LoginForm = ({ onLogoClick }: LoginFormProps) => {
  const {
    control,
    email,
    handleBackButtonClick,
    handleForgotPasswordButtonClick,
    handleForwardButtonClick,
    isForwardButtonDisabled,
    showBackButton,
    step,
    validationRules,
    isLoading,
    forwardButtonLabel,
    stepTitle,
    showAccessRestrictionDialog,
    toggleAccessRestrictionDialog,
    hospitalLogoUrl,
  } = useLoginForm();

  const { translation } = useTranslationContext();
  // Using subpath method for signup route since it's accessed via anchor link
  // Note: Anchor links require absolute paths, so subpath logic must be preserved
  const signupPath = useCobrandedPathWithSubpath('/signup');

  const {
    showSplash: showLoginSplash,
    showSpinner,
    isLogoVisible,
    splashLogoUrl,
    handleLogoLoad,
    handleLogoError,
  } = useSplash({
    isLoading,
    hospitalLogoUrl,
    isEnabled: step === LoginFormStep.PASSWORD,
  });

  return (
    <>
      {showLoginSplash && (
        <HospitalLogoSplash
          logoUrl={splashLogoUrl}
          showSpinner={showSpinner}
          isLogoVisible={isLogoVisible}
          onLogoLoad={handleLogoLoad}
          onLogoError={handleLogoError}
        />
      )}
      <Stack spacing={2}>
        <form
          id="login-form"
          data-testid={`login-form__${step}`}
          onSubmit={handleForwardButtonClick}
        >
          <Stack direction="column" spacing={5}>
            <Stack direction="row" alignItems="center" justifyContent="center">
              {showBackButton && (
                <BackButton id="login-form__back-button" onClick={handleBackButtonClick} />
              )}
              <Stack width="100%" alignItems="center">
                <LoginStepHeader title={stepTitle} onLogoClick={onLogoClick} />
              </Stack>
              {showBackButton && <BackButton disabled sx={{ visibility: 'hidden' }} />}
            </Stack>
            {step === LoginFormStep.EMAIL && (
              <LoginEmailStep control={control} validationRules={validationRules.email} />
            )}
            {step === LoginFormStep.PASSWORD && (
              <LoginPasswordStep
                control={control}
                email={email}
                validationRules={validationRules.password}
              />
            )}
            {step === LoginFormStep.FORGOT_PASSWORD && (
              <LoginForgotPasswordStep control={control} validationRules={validationRules.email} />
            )}
            {step === LoginFormStep.PASSWORD_RESET_CONFIRMATION && (
              <LoginPasswordResetConfirmationStep />
            )}
            <Stack direction="column" spacing={1}>
              <Button
                id={`login-form__forward-button-${step}`}
                isLoading={isLoading && step !== LoginFormStep.PASSWORD}
                variant="primary"
                disabled={isForwardButtonDisabled}
                type="submit"
                size="xlarge"
              >
                {forwardButtonLabel}
              </Button>
              {step === LoginFormStep.EMAIL && (
                <Typography variant="body1" textAlign="center" fontSize="18px" paddingTop={3}>
                  {translation('dont_have_account')}{' '}
                  {/* IMPORTANT : Using anchor link (href) instead of React Router navigation to ensure
                  the beforeunload event triggers properly on browser back button from signup page.
                  This maintains the expected browser back button behavior throughout the signup flow. */}
                  <Link id="login-form__sign-up-link" href={signupPath}>
                    {translation('sign_up')}
                  </Link>
                </Typography>
              )}
              {step === LoginFormStep.PASSWORD && (
                <Button
                  id="login-form__forgot-password-button"
                  variant="text"
                  type="button"
                  onClick={handleForgotPasswordButtonClick}
                >
                  {translation('forgot_password')}
                </Button>
              )}
            </Stack>
          </Stack>
        </form>
        <VersionInfo />
        <AccessRestrictionDialog
          open={showAccessRestrictionDialog}
          title={translation('web_ineligible')}
          body={translation('web_ineligible_log_in')}
          primaryCtaButton={{
            label: translation('general_ok'),
            onClick: toggleAccessRestrictionDialog,
          }}
          onClose={toggleAccessRestrictionDialog}
        />
      </Stack>
    </>
  );
};
